import React, { useCallback, useMemo, useState } from "react";
import {
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  Chip,
  Box,
  Divider,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Checkbox,
  ListItemButton,
  ListItemIcon,
  MenuItem,
  Stack,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ViewList as ViewListIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

const GroupPane = ({
  tags,
  addTagToGroup,
  removeTagFromGroup,
  addMultipleTagsToGroup,
  deleteGroup,
  renameGroup,
  setInfoMessages,
}) => {
  const [newGroupDialogOpen, setNewGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [renameGroupDialogOpen, setRenameGroupDialogOpen] = useState(false);
  const [renameGroupName, setRenameGroupName] = useState("");
  const [groupToRename, setGroupToRename] = useState("");
  const [dragOverGroup, setDragOverGroup] = useState(null);
  const [selectedTagIndices, setSelectedTagIndices] = useState(new Set());
  const [targetGroupForSelection, setTargetGroupForSelection] = useState("");
  const [persistentGroups, setPersistentGroups] = useState([]);
  const [groupSearchTerm, setGroupSearchTerm] = useState("");
  const [tagSearchTerm, setTagSearchTerm] = useState("");

  const filteredTags = useMemo(() => {
    if (tagSearchTerm === "") {
      return tags;
    }
    return tags.filter(
      (tag) =>
        tag.TagName &&
        tag.TagName.toLowerCase().includes(tagSearchTerm.toLowerCase())
    );
  }, [tags, tagSearchTerm]);

  // Create a mapping of filtered tags to their original indices for efficient lookup
  const filteredTagIndices = useMemo(() => {
    const indices = new Map();
    filteredTags.forEach((tag, filteredIndex) => {
      const originalIndex = tags.findIndex((t) => t === tag);
      indices.set(filteredIndex, originalIndex);
    });
    return indices;
  }, [filteredTags, tags]);

  const groups = useMemo(() => {
    const groupMap = new Map();

    persistentGroups.forEach((groupName) => {
      groupMap.set(groupName, []);
    });

    tags.forEach((tag, index) => {
      if (tag.GroupName) {
        const groupNames = tag.GroupName.split(";")
          .map((name) => name.trim())
          .filter((name) => name.length > 0);

        groupNames.forEach((groupName) => {
          if (!groupMap.has(groupName)) {
            groupMap.set(groupName, []);
          }
          groupMap.get(groupName).push({
            index,
            TagName: tag.TagName,
            TagIdentifier: tag.TagIdentifier,
            AssetNumber: tag.AssetNumber,
            GroupName: tag.GroupName,
          });
        });
      }
    });

    return Array.from(groupMap.entries())
      .map(([groupName, members]) => ({
        name: groupName,
        members: members.sort((a, b) => a.TagName.localeCompare(b.TagName)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter(
        (group) =>
          groupSearchTerm === "" ||
          group.name.toLowerCase().includes(groupSearchTerm.toLowerCase())
      );
  }, [tags, persistentGroups, groupSearchTerm]);

  const handleCreateGroup = useCallback(() => {
    const groupName = newGroupName.trim();
    if (groupName) {
      setPersistentGroups((prev) => [...prev, groupName]);
      setNewGroupName("");
      setNewGroupDialogOpen(false);
      setInfoMessages({
        type: "snackbar",
        message: `Created new group "${groupName}".`,
        severity: "success",
        open: true,
      });
    }
  }, [newGroupName, setInfoMessages]);

  const handleDragOver = useCallback((e, groupName) => {
    e.preventDefault();
    setDragOverGroup(groupName);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOverGroup(null);
  }, []);

  const handleDrop = useCallback(
    (e, groupName) => {
      e.preventDefault();
      setDragOverGroup(null);

      try {
        const tagData = JSON.parse(e.dataTransfer.getData("application/json"));
        if (tagData && typeof tagData.index === "number") {
          addTagToGroup(tagData.index, groupName);
        }
      } catch (error) {
        console.error("Error processing dropped Tag:", error);
      }
    },
    [addTagToGroup]
  );

  const handleTagSelection = useCallback(
    (tagIndex) => {
      const newSelected = new Set(selectedTagIndices);
      if (newSelected.has(tagIndex)) {
        newSelected.delete(tagIndex);
      } else {
        newSelected.add(tagIndex);
      }
      setSelectedTagIndices(newSelected);
    },
    [selectedTagIndices]
  );

  const handleAddSelectedToGroup = useCallback(() => {
    if (targetGroupForSelection && selectedTagIndices.size > 0) {
      addMultipleTagsToGroup(
        Array.from(selectedTagIndices),
        targetGroupForSelection
      );
      setSelectedTagIndices(new Set());
      setTargetGroupForSelection("");
    }
  }, [targetGroupForSelection, selectedTagIndices, addMultipleTagsToGroup]);

  const handleDeleteGroup = useCallback(
    (groupName) => {
      if (deleteGroup) {
        deleteGroup(groupName);
      }
      setPersistentGroups((prev) => {
        return prev.filter((name) => name !== groupName);
      });
    },
    [deleteGroup]
  );

  const handleRenameGroup = useCallback((groupName) => {
    setGroupToRename(groupName);
    setRenameGroupName(groupName);
    setRenameGroupDialogOpen(true);
  }, []);

  const handleConfirmRename = useCallback(() => {
    const newName = renameGroupName.trim();
    if (newName && newName !== groupToRename) {
      if (renameGroup) {
        renameGroup(groupToRename, newName);
      }
      setPersistentGroups((prev) => {
        return prev.filter((name) => name !== groupToRename).concat(newName);
      });
      setRenameGroupDialogOpen(false);
      setRenameGroupName("");
      setGroupToRename("");
    }
  }, [renameGroupName, groupToRename, renameGroup]);

  const handleSelectAll = useCallback(() => {
    if (selectedTagIndices.size === filteredTags.length) {
      setSelectedTagIndices(new Set());
    } else {
      // Use the filtered tag indices mapping to get correct original indices
      const allFilteredIndices = Array.from(
        { length: filteredTags.length },
        (_, i) => i
      );
      const originalIndices = allFilteredIndices.map((filteredIndex) =>
        filteredTagIndices.get(filteredIndex)
      );
      setSelectedTagIndices(new Set(originalIndices));
    }
  }, [selectedTagIndices.size, filteredTags.length, filteredTagIndices]);

  // Helper function to get groups for a specific tag
  const getTagGroups = useCallback((tag) => {
    if (!tag.GroupName) return [];
    return tag.GroupName.split(";");
  }, []);

  const makeTagDraggable = useCallback((tag, index) => {
    const handleDragStart = (e) => {
      const dragData = {
        index: index,
        tagName: tag.TagName,
        tagIdentifier: tag.TagIdentifier,
      };
      e.dataTransfer.setData("application/json", JSON.stringify(dragData));
      e.dataTransfer.effectAllowed = "move";
    };

    return handleDragStart;
  }, []);

  const getTagInfoStack = useCallback(
    (tag) => {
      return (
        <Stack direction='row' spacing={1}>
          <Typography variant='body2' fontWeight='medium' noWrap>
            {tag.TagName || "Unnamed Tag"}
          </Typography>
          {tag.AssetNumber && (
            <Typography variant='caption' color='text.secondary' noWrap>
              AssetNumber: {tag.AssetNumber}
            </Typography>
          )}
          <Typography variant='caption' color='text.secondary' noWrap>
            TagID: {tag.TagIdentifier || "N/A"}
          </Typography>
          {getTagGroups(tag).length > 0 &&
            getTagGroups(tag).map((groupName) => (
              <Chip
                key={groupName}
                label={groupName}
                size='small'
                variant='outlined'
                color='primary'
                sx={{ fontSize: "0.7rem", height: 20 }}
              />
            ))}
        </Stack>
      );
    },
    [getTagGroups]
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper
          sx={{
            p: 2,
            height: "fit-content",
            minWidth: 300,
            maxHeight: "80vh",
            overflow: "auto",
          }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <GroupIcon />
              <Typography variant='h6'>Tag Groups ({groups.length})</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                size='small'
                placeholder='Search Tag Groups...'
                value={groupSearchTerm}
                onChange={(e) => {
                  setGroupSearchTerm(e.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      sx={{ color: "text.secondary", mr: 1, fontSize: 20 }}
                    />
                  ),
                }}
                sx={{ width: 200 }}
              />
              <IconButton
                size='small'
                onClick={() => setNewGroupDialogOpen(true)}
                color='primary'>
                <AddIcon />
              </IconButton>
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {groups.map((group) => (
            <Accordion
              key={group.name}
              defaultExpanded={false}
              sx={{
                mb: 1,
                "&.Mui-expanded": {
                  margin: "8px 0",
                },
              }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                  backgroundColor:
                    dragOverGroup === group.name
                      ? "action.selected"
                      : "transparent",
                  border: dragOverGroup === group.name ? "2px dashed" : "none",
                  borderColor: "primary.main",
                }}
                onDragOver={(e) => handleDragOver(e, group.name)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, group.name)}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                  }}>
                  <Typography variant='subtitle1' sx={{ flexGrow: 1 }}>
                    {group.name}
                  </Typography>
                  <Chip
                    label={group.members.length}
                    size='small'
                    color='primary'
                    variant='outlined'
                  />
                  <IconButton
                    size='small'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRenameGroup(group.name);
                    }}
                    color='primary'
                    sx={{ ml: 1 }}>
                    <EditIcon fontSize='small' />
                  </IconButton>
                  <IconButton
                    size='small'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteGroup(group.name);
                    }}
                    color='error'
                    sx={{ ml: 1 }}>
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <List dense>
                  {group.members.map((member) => (
                    <ListItem
                      key={`${group.name}-${member.index}`}
                      sx={{ py: 0.5 }}>
                      {getTagInfoStack(member)}
                      <IconButton
                        size='small'
                        onClick={() => {
                          if (
                            groups.find((g) => g.name === group.name).members
                              .length === 1
                          ) {
                            removeTagFromGroup(member.index, group.name);
                            setPersistentGroups((prev) => [
                              ...prev,
                              group.name,
                            ]);
                          } else {
                            removeTagFromGroup(member.index, group.name);
                          }
                        }}
                        color='error'
                        sx={{ ml: 1 }}>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}

          <Dialog
            open={newGroupDialogOpen}
            onClose={() => setNewGroupDialogOpen(false)}>
            <DialogTitle>Create New Tag Group</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin='dense'
                label='Tag Group Name'
                fullWidth
                variant='outlined'
                value={newGroupName}
                onChange={(e) => {
                  setNewGroupName(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleCreateGroup();
                  }
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setNewGroupDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateGroup} variant='contained'>
                Create
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper
          sx={{
            p: 2,
            height: "fit-content",
            minWidth: 300,
            maxHeight: "80vh",
            overflow: "auto",
          }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ViewListIcon />
              <Typography variant='h6'>
                All Tags ({filteredTags.length})
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                size='small'
                placeholder='Search Tags...'
                value={tagSearchTerm}
                onChange={(e) => {
                  setTagSearchTerm(e.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      sx={{ color: "text.secondary", mr: 1, fontSize: 20 }}
                    />
                  ),
                }}
                sx={{ width: 200 }}
              />
              <Button size='small' onClick={handleSelectAll} variant='outlined'>
                {selectedTagIndices.size === filteredTags.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {selectedTagIndices.size > 0 && (
            <Box sx={{ mb: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Add {selectedTagIndices.size} selected Tag(s) to group:
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  select
                  size='small'
                  value={targetGroupForSelection}
                  onChange={(e) => {
                    setTargetGroupForSelection(e.target.value);
                  }}
                  sx={{ minWidth: 200 }}
                  label='Select Group'>
                  {groups.map((group) => (
                    <MenuItem key={group.name} value={group.name}>
                      {group.name}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant='contained'
                  size='small'
                  onClick={handleAddSelectedToGroup}
                  disabled={!targetGroupForSelection}>
                  Add to Group
                </Button>
              </Box>
            </Box>
          )}

          <List dense>
            {filteredTags.map((tag, filteredIndex) => {
              const originalIndex = filteredTagIndices.get(filteredIndex);
              return (
                <ListItem
                  key={originalIndex}
                  sx={{ py: 0.5 }}
                  draggable
                  onDragStart={makeTagDraggable(tag, originalIndex)}>
                  <ListItemButton
                    dense
                    onClick={() => handleTagSelection(originalIndex)}
                    sx={{ p: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        edge='start'
                        checked={selectedTagIndices.has(originalIndex)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    {getTagInfoStack(tag)}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      </Grid>

      <Dialog
        open={renameGroupDialogOpen}
        onClose={() => setRenameGroupDialogOpen(false)}>
        <DialogTitle>Rename Tag Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='New Tag Group Name'
            fullWidth
            variant='outlined'
            value={renameGroupName}
            onChange={(e) => {
              setRenameGroupName(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleConfirmRename();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameGroupDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirmRename} variant='contained'>
            Rename
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default GroupPane;
